import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import debounce from 'lodash/debounce';
import withStyles from 'material-ui/styles/withStyles';
import TextField from 'material-ui/TextField';
import Tooltip from 'material-ui/Tooltip';
import { onEnter } from 'spark-util/keyboardEvents';
import { filterGrid } from '../state/gridActions';
import { getFilter } from '../state/gridSelectors';
import { useGridContext } from './gridContext';

const styles = theme => ({
    txtField: {
        margin: 0,
        width: 200,
        border: '1px solid #c8c8c8'
    },
    inputText: {
        color: '#595959',
        paddingLeft: '.2em',
        fontSize: '13px'
    }
});

const hasMinSearchLength = (s) => (s || '').trim().length >= 2;

const Search = ({ placeHolder, classes }) => {
    const gridName = useGridContext();
    const dispatch = useDispatch();
    const initialSearch = useSelector(state => {
        const currentFilter = getFilter(state, gridName);
        return currentFilter && currentFilter.searchParam;
    });

    const [currentSearch, setCurrentSearch] = useState(initialSearch || '');
    const currentSearchRef = useRef(currentSearch);
    currentSearchRef.current = currentSearch;

    const triggerSearch = useMemo(
        () => debounce(() => {
            const queryStr = hasMinSearchLength(currentSearchRef.current)
                ? currentSearchRef.current
                : '';
            dispatch(filterGrid(gridName, { searchParam: queryStr.trim() }));
        }, 500),
        [dispatch, gridName]
    );

    const onChange = useCallback(({ target: { value } }) => {
        // Read previous text before updating the ref; otherwise prev === next and we never debounce-search.
        const prevTrim = (currentSearchRef.current || '').trim();
        const nextTrim = (value || '').trim();
        setCurrentSearch(value);
        currentSearchRef.current = value;
        const shouldFetch =
            (nextTrim.length >= 2 && nextTrim !== prevTrim) ||
            (prevTrim.length >= 2 && nextTrim.length < 2);
        if (shouldFetch) {
            triggerSearch();
        }
    }, [triggerSearch]);

    const onKeyPress = useMemo(() => onEnter(e => e.preventDefault()), []);

    return (
        <div>
            <Tooltip 
                title="Please enter 2-200 characters to search"
                placement="bottom"
            >
            <TextField
                placeholder={placeHolder}
                className={classes.txtField}
                value={currentSearch}
                onChange={onChange}
                onKeyPress={onKeyPress}
                InputProps={{
                    disableUnderline: true,
                    classes: {
                        input: classes.inputText
                    }
                }}
                type="search"
                autoComplete="search"
                margin="normal"
            />
            </Tooltip>
        </div>
    );
};

const SearchField = withStyles(styles)(Search);

export default SearchField;
